// The counter display at one desk: the pure part. Its page is pages/CounterDisplay.tsx;
// the contract is docs/design/redesign/contracts/counter-display.md.
import type { CounterScreen } from '../layout/types'
import type { BranchState } from './branchState'
import { serviceName, type DeskPill, type Language } from './kioskText'
import type { Announcement, CounterState, CounterView, Unit } from './types'

/** The ticket at the desk, as GET /screen/{branch}/{unit}/desk answers it. */
export interface CounterVisit {
  ticketId: string
  displayNumber: string
  /** The ticket's current service's name, by language; absent if unknown. */
  service?: Record<string, string>
  /** Values of the visit fields declared for the display surface; absent when none. */
  fields?: Record<string, string>
}

/** How long a call or recall flashes the number. */
export const FLASH_MS = 4000

/** The desk this display stands at, from the live state; undefined until the snapshot names it. */
export function deskOf(state: BranchState | null, unit: Pick<Unit, 'desks'> | null): CounterView | undefined {
  const id = unit?.desks?.[0]
  if (!id) return undefined
  return state?.counters.find((c) => c.counterId === id)
}

/** Called while a ticket at the desk is called or being served; Free otherwise. */
export function counterScreen(desk: CounterView | undefined): CounterScreen {
  if (!desk?.ticketId) return 'free'
  return desk.ticketState === 'called' || desk.ticketState === 'serving' ? 'called' : 'free'
}

/** The desk's own state, which the Free screen words: Free, Back soon or Closed. */
export function deskStatusOf(desk: CounterView | undefined): CounterState | undefined {
  const s = desk?.state
  return s === 'open' || s === 'paused' || s === 'closed' ? s : undefined
}

/** The desk's number alone: the code's last digits without leading zeros ("D04" is 4),
 *  else the whole code. The code, not the name, because the name is free text. */
export function deskNumberOf(desk: Pick<CounterView, 'code'> | undefined): string {
  const code = desk?.code ?? ''
  const digits = /(\d+)\D*$/.exec(code)?.[1]
  return digits ? String(Number(digits)) : code
}

/** What the status pill says: the ticket's state while one is at the desk, else the desk's. */
export function deskPillOf(desk: CounterView | undefined): DeskPill | undefined {
  if (counterScreen(desk) === 'called') return desk?.ticketState === 'serving' ? 'serving' : 'called'
  return deskStatusOf(desk)
}

/** The visit to draw: only the one for the ticket now on the Called screen. */
export function visitShown(visit: CounterVisit | null, desk: CounterView | undefined): CounterVisit | null {
  if (!visit || counterScreen(desk) !== 'called') return null
  return visit.ticketId === desk?.ticketId ? visit : null
}

/** The service's name in the screen's language; the hall board's rule for an unset language. */
export function visitService(visit: CounterVisit | null, chosen: Language | undefined): string {
  const name = visit?.service
  if (!name) return ''
  return chosen ? serviceName(name, chosen, '') : (name.en ?? '')
}

/** A call or recall being flashed: which one, and until when on this screen's clock. */
export interface Flash {
  key: string
  until: number
  /** A recall of the ticket already shown, rather than a new call. */
  recall: boolean
}

/** The flash after an announcement: a call or recall for this desk starts one;
 *  the same announcement delivered twice does not restart it. */
export function flashAfter(prev: Flash | null, a: Announcement, deskId: string | undefined, now: number): Flash | null {
  if (!deskId || a.counterId !== deskId) return prev
  const key = `${a.ticketId}:${a.at}`
  if (prev?.key === key) return prev
  return { key, until: now + FLASH_MS, recall: a.repeat }
}

/** What the screen's entrance replays on: a switch of screen, or a new ticket called. */
export function entranceKey(screen: CounterScreen, desk: CounterView | undefined): string {
  return screen === 'called' ? `called:${desk?.ticketId ?? ''}` : 'free'
}

/**
 * Which entrance is running. A CSS animation restarts only when its name changes, so
 * each replay flips between two identical keyframes ('a' and 'b'); the number's
 * recall has its own pair, cleared by the next entrance so that entrance reaches it,
 * and the ring round the number (a call or a recall) has a third.
 */
export interface Motion {
  enter: string
  entered: 'a' | 'b'
  recall: string
  recalled: '' | 'a' | 'b'
  ring: string
  ringed: '' | 'a' | 'b'
}

export const STILL: Motion = { enter: '', entered: 'b', recall: '', recalled: '', ring: '', ringed: '' }

const other = (was: '' | 'a' | 'b'): 'a' | 'b' => (was === 'a' ? 'b' : 'a')

/** The motion after this render: a new entrance key replays everything, a new recall
 *  the number only; each new call or recall rings the number. */
export function motionAfter(prev: Motion, enter: string, flash: Flash | null): Motion {
  const recall = flash?.recall ? flash.key : ''
  let next = prev
  if (enter !== prev.enter) {
    next = { ...prev, enter, entered: other(prev.entered), recall, recalled: '' }
  } else if (recall && recall !== prev.recall) {
    next = { ...prev, recall, recalled: other(prev.recalled) }
  }
  const ring = flash?.key ?? ''
  if (ring && ring !== next.ring) next = { ...next, ring, ringed: other(next.ringed) }
  return next
}

/**
 * Asks the server about the visit at the desk: when the desk's ticket changes and
 * once after each (re)connect, never on a timer. An answer for a ticket no longer
 * at the desk is dropped; a refusal or a failure keeps what is shown.
 */
export class VisitAsker {
  private ticketId = ''
  private desk = ''
  private connected = false
  private asked = 0
  private taken = 0

  constructor(
    private readonly fetch: () => Promise<{ visit: CounterVisit | null; clerk?: string }>,
    private readonly onVisit: (visit: CounterVisit | null) => void,
    private readonly onClerk?: (clerk: string) => void,
  ) {}

  /** What the live state says now: the desk's ticket, and whether the channel is up.
   *  `desk` is the desk's state when the design shows the clerk ('' otherwise): a
   *  sign-in or out changes it, so a free desk asks again too. */
  see(ticketId: string, connected: boolean, desk = ''): void {
    const changed = ticketId !== this.ticketId
    const deskChanged = desk !== this.desk
    const reconnected = connected && !this.connected
    this.ticketId = ticketId
    this.desk = desk
    this.connected = connected
    if (!ticketId) {
      if (changed) this.onVisit(null)
      if (desk && connected && (changed || deskChanged || reconnected)) this.ask()
      return
    }
    if (connected && (changed || deskChanged || reconnected)) this.ask()
  }

  private ask(): void {
    const mine = ++this.asked
    const askedFor = this.ticketId
    this.fetch()
      .then(({ visit, clerk }) => {
        if (mine < this.taken) return
        if (this.ticketId !== askedFor) return
        if (visit && visit.ticketId !== askedFor) return
        this.taken = mine
        if (askedFor) this.onVisit(visit)
        this.onClerk?.(clerk ?? '')
      })
      .catch(() => undefined)
  }
}
